<template>
  <div :data-test="dataTest">
    <v-col
      cols="12"
      :sm="!editMode ? 8 : 12"
      :class="{
        'pa-0': removeColPadding
      }"
    >
      <v-card
        v-if="!editMode"
        color="white"
        class="pa-4"
        style="min-height: 85px"
      >
        <v-btn
          v-if="!disabled"
          class="edit-green float-end"
          icon
          data-test="editAddress"
          small
          @click="openEditForm()"
        >
          <v-icon color="white" small>$edit</v-icon>
        </v-btn>

        <template v-if="address">
          <AppShowAddress :address="address" break-up />
        </template>
      </v-card>

      <v-form v-else :disabled="disabled">
        <span
          v-if="!hideEditHeading"
          style="font-size: 16px; font-weight: bold"
        >
          {{ $t("common:addressComponent.editView.heading", "Address ") }}
        </span>

        <v-text-field
          v-if="v$.form.addressText"
          v-model="form.addressText"
          :disabled="fieldDisabled('addressText')"
          :error-messages="fieldErrorMessages('addressText')"
          class="entsys--white-background-input"
          :label="labels.addressText"
          outlined
          data-test="addressText"
          @blur="v$.form.addressText.$touch()"
        />
        <v-row
          :no-gutters="$vuetify.breakpoint.xsOnly"
          :dense="$vuetify.breakpoint.smAndDown"
        >
          <v-col cols="12" sm="7">
            <v-text-field
              v-if="v$.form.addressHouse"
              v-model="form.addressHouse"
              :disabled="fieldDisabled('addressHouse')"
              :error-messages="fieldErrorMessages('addressHouse')"
              class="entsys--white-background-input"
              :label="labels.addressHouse"
              outlined
              data-test="addressHouse"
              @blur="v$.form.addressHouse.$touch()"
            />
          </v-col>
          <v-col cols="12" sm="5">
            <v-text-field
              v-if="v$.form.addressBox"
              v-model="form.addressBox"
              :disabled="fieldDisabled('addressBox')"
              :error-messages="fieldErrorMessages('addressBox')"
              class="entsys--white-background-input"
              :label="labels.addressBox"
              outlined
              data-test="addressBox"
              @blur="v$.form.addressBox.$touch()"
            />
          </v-col>
        </v-row>

        <v-text-field
          v-if="v$.form.addressCity"
          v-model="form.addressCity"
          :disabled="fieldDisabled('addressCity')"
          :error-messages="fieldErrorMessages('addressCity')"
          class="entsys--white-background-input"
          :label="labels.addressCity"
          outlined
          data-test="addressCity"
          @blur="v$.form.addressCity.$touch()"
        />

        <v-text-field
          v-if="v$.form.addressState"
          v-model="form.addressState"
          :disabled="fieldDisabled('addressState')"
          :error-messages="fieldErrorMessages('addressState')"
          class="entsys--white-background-input"
          :label="labels.addressState"
          outlined
          data-test="addressState"
          @blur="v$.form.addressState.$touch()"
        />

        <v-text-field
          v-if="v$.form.addressIndex"
          v-model="form.addressIndex"
          :disabled="fieldDisabled('addressIndex')"
          :error-messages="fieldErrorMessages('addressIndex')"
          class="entsys--white-background-input"
          :label="labels.addressIndex"
          outlined
          data-test="addressIndex"
          @blur="v$.form.addressIndex.$touch()"
        />

        <AppSelectCountries
          v-if="v$.form.addressCountry"
          v-model="form.addressCountry"
          :label="labels.addressCountry"
          class="entsys--white-background-input"
          data-test="addressCountry"
          :input-props="{
            disabled: fieldDisabled('addressCountry'),
            errorMessages: fieldErrorMessages('addressCountry'),
            returnObject: true
          }"
          clearable
          @change="
            addressCountry =>
              (form.addressCountryId = addressCountry
                ? addressCountry.id
                : null)
          "
          @blur="v$.form.addressCountry.$touch()"
        />

        <AppBtnsRow v-if="!formModeOnly">
          <template #right="{ attributes }">
            <v-btn
              data-test="addressSubmit"
              v-bind="attributes"
              outlined
              @click="submitAddress"
              >{{
                $t("common:addressComponent.editView.ok.button", "Ok")
              }}</v-btn
            ></template
          >
        </AppBtnsRow>
      </v-form>
    </v-col>
  </div>
</template>
<script>
import gql from "fraql";

export default defineComponent({
  name: "AppAddress",
  fragments: {
    addressFields: gql`
      fragment _ on Address {
        id
        addressText
        addressHouse
        addressCity
        addressIndex
        addressState
        addressBox
        addressCountryId
        addressCountry @client {
          id
          name
          ${AppSelectCountries.fragments.countryFields}
        }
      }
    `
  }
});

/**
 * Remove properties that AddressInput does not want it to be included
 * @param {object} address
 */
export function prepAddressForAddressInput(address) {
  if (!address || typeof address != "object") return null;
  address = clone(address);
  delete address.id;
  delete address.addressCountry;
  delete address.__typename;
  delete address.contactableType;
  return address;
}
</script>
<script setup>
import {
  defineComponent,
  ref,
  watch,
  computed,
  getCurrentInstance,
  nextTick
} from "vue";
import {
  useFormValidation,
  BasicFormFieldFragment
} from "@composables/form/form_validation";
import { clone } from "lodash-es";

// i19next-vue
import { useTranslation } from "@/plugins/i18next/i18next";
import { useQuery } from "@vue/apollo-composable";

import AppSelectCountries from "@AppSelects/AppSelectCountries.vue";
import AppBtnsRow from "@elements/globals/AppBtnsRow.vue";
import AppShowAddress from "@AppShows/AppShowAddress.vue";

const props = defineProps({
  addressForm: {
    type: Object,
    default: () => ({})
  },
  formModeOnly: {
    type: Boolean,
    default: false
  },

  addressObject: {
    type: Object,
    default: null
  },
  disabled: {
    type: Boolean,
    default: false
  },
  dataTest: {
    type: String,
    default: undefined
  },

  contactableId: {
    type: String,
    default: null
  },
  contactableType: {
    type: String,
    required: true
  },
  contactableAssociationName: {
    type: String,
    required: true
  },
  parentFormName: {
    type: String,
    default: undefined
  },

  hideEditHeading: {
    type: Boolean,
    default: false
  },
  removeColPadding: {
    type: Boolean,
    default: false
  }
});

const emits = defineEmits([
  "set-address", // This simply emits address data to parent. Nothing special
  "edit-mode-state",
  "update:addressForm"
]);

const { t } = useTranslation();
const instance = getCurrentInstance();

const labels = computed(() => ({
  addressText: t(
    "common:addressComponent.editView.streetAddress.label",
    "Street address"
  ),
  addressHouse: t(
    "common:addressComponent.editView.buildingNr.label",
    "Building nr"
  ),
  addressCity: t(
    "common:addressComponent.editView.city.label",
    "City or municipality"
  ),
  addressIndex: t(
    "common:addressComponent.editView.postalCode.label",
    "Postal code"
  ),
  addressState: t(
    "common:addressComponent.editView.provinceOrRegion.label",
    "Region or province"
  ),
  addressBox: t("common:addressComponent.editView.PObox.label", "PO box"),
  addressCountry: t("common:addressComponent.editView.country.label", "Country")
}));

const editModeValue = ref(false);
const editMode = computed({
  get() {
    return props.formModeOnly || editModeValue.value;
  },
  set(v) {
    if (v == false && instance.proxy.$el.getBoundingClientRect().top < 0) {
      instance.proxy.$el.scrollIntoView();
    }
    editModeValue.value = v;
    emits("edit-mode-state", v);
  }
});

const address = ref({});

const form = ref({
  id: null,
  contactableType: props.contactableType,
  addressText: null,
  addressHouse: null,
  addressCity: null,
  addressIndex: null,
  addressState: null,
  addressBox: null,
  addressCountryId: null,
  addressCountry: null
});

watch(
  form,
  v => {
    emits("update:addressForm", v);
  },
  { deep: true }
);

const { onResult: onInitFormResult } = useQuery(
  gql`
    query initAddressForm(
      $contactableId: ID
      $contactableType: String!
      $contactableAssociationName: String!
      $parentFormName: String
    ) {
      initAddressForm(
        contactableId: $contactableId
        contactableType: $contactableType
        contactableAssociationName: $contactableAssociationName
        parentFormName: $parentFormName
      ) {
        vuelidateValidations
        readOnlyFields
        formFields {
          ${BasicFormFieldFragment}
        }
      }
    }
  `,
  () => ({
    contactableId: props.contactableId,
    contactableType: props.contactableType,
    contactableAssociationName: props.contactableAssociationName,
    parentFormName: props.parentFormName
  })
);

const initAddressForm = ref({
  vuelidateValidations: {},
  readOnlyFields: [],
  formFields: []
});

onInitFormResult(({ loading, data }) => {
  if (loading) return;
  initAddressForm.value = data.initAddressForm;

  nextTick().then(() => {
    // When in form mode, don't validate right away and editMode is true anyway
    if (props.formModeOnly) return;
    formValidate();
    editMode.value = editMode.value ? true : v$.value.form.$invalid;

    if (!props.disabled && Object.values(address.value).every(x => x == null)) {
      editMode.value = true;
    }
  });
});

const {
  v$,
  fieldDisabled,
  fieldErrorMessages,
  formErrorMessages,
  formValidate
} = useFormValidation(initAddressForm, form, {
  mapToModel: {
    // map API field names to form field names if they are different
    addressCountryId: "addressCountry"
  },
  customDefaultTranslationLabels: labels
});

function setForm(c) {
  if (!c) c = {};
  form.value = {
    id: c.id ?? null,
    contactableType: props.contactableType,
    addressText: c.addressText ?? null,
    addressHouse: c.addressHouse ?? null,
    addressCity: c.addressCity ?? null,
    addressIndex: c.addressIndex ?? null,
    addressState: c.addressState ?? null,
    addressBox: c.addressBox ?? null,
    addressCountryId: c.addressCountryId ?? null,
    addressCountry: c.addressCountry ?? null
  };
}

function openEditForm() {
  setForm(address.value);
  editMode.value = true;
}

function submitAddress() {
  if (!formValidate()) {
    return;
  }

  address.value = clone(form.value);
  emits("set-address", form.value);
  editMode.value = false;
}

setForm(props.addressObject);
address.value = props.addressObject ?? {};

defineExpose({
  formValidate,
  formErrorMessages
});
</script>
